import { Upload } from 'tus-js-client'
import { supabase } from './supabase'

/**
 * Envoi d'un gros fichier (plan PDF) vers le stockage, par morceaux de 6 Mo.
 *
 * Avantages sur un envoi d'un seul bloc :
 *  - une barre de progression réelle ;
 *  - si la connexion coupe, l'envoi reprend là où il s'était arrêté au lieu
 *    de tout recommencer (utile en 4G ou sur un Wi-Fi de chantier).
 *
 * La taille maximale d'un fichier est fixée par Supabase : 50 Mo sur le plan
 * gratuit ; jusqu'à 500 Go sur le plan Pro (réglage « Global file size limit »
 * dans Storage → Settings).
 */
export async function televerserGros(
  bucket: string,
  chemin: string,
  fichier: File,
  progression: (fraction: number) => void
): Promise<void> {
  const { data } = await supabase.auth.getSession()
  const jeton = data.session?.access_token
  if (!jeton) throw new Error('Session expirée : reconnecte-toi.')

  const url = import.meta.env.VITE_SUPABASE_URL as string
  const cle = import.meta.env.VITE_SUPABASE_ANON_KEY as string
  // Point d'entrée dédié aux envois par morceaux, recommandé par Supabase.
  const ref = new URL(url).hostname.split('.')[0]
  const pointEntree = url.includes('.supabase.co')
    ? `https://${ref}.storage.supabase.co/storage/v1/upload/resumable`
    : `${url}/storage/v1/upload/resumable`

  await new Promise<void>((resolve, reject) => {
    const envoi = new Upload(fichier, {
      endpoint: pointEntree,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: { authorization: `Bearer ${jeton}`, apikey: cle, 'x-upsert': 'false' },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName: bucket,
        objectName: chemin,
        contentType: fichier.type || 'application/pdf',
        cacheControl: '3600'
      },
      chunkSize: 6 * 1024 * 1024, // valeur imposée par Supabase
      onError: (e) => reject(new Error(messageClair(e))),
      onProgress: (envoye, total) => progression(total ? envoye / total : 0),
      onSuccess: () => resolve()
    })
    // Reprend un envoi interrompu du même fichier, s'il y en a un.
    void envoi.findPreviousUploads().then((precedents) => {
      if (precedents.length > 0) envoi.resumeFromPreviousUpload(precedents[0])
      envoi.start()
    })
  })
}

/** Traduit les refus du serveur en message compréhensible. */
export function messageClair(e: unknown): string {
  const brut = e instanceof Error ? e.message : String(e)
  if (/maximum allowed size|413|Payload too large|exceeded/i.test(brut)) {
    return (
      "Ce fichier dépasse la taille maximale acceptée par le stockage. Sur le plan gratuit " +
      "de Supabase, la limite est de 50 Mo par fichier ; il faut passer au plan Pro et " +
      'augmenter la limite (Storage → Settings → « Global file size limit »).'
    )
  }
  return brut
}
